<template>
    <div class="content product-down-detail-page">
        <div class="container">
            <div v-if="loading" class="loading">불러오는 중...</div>
            <div v-else-if="!boardVO || (!boardVO.idx && !gid)" class="empty">게시글을 찾을 수 없습니다.</div>
            <div v-else class="product-detail-content">
                <!-- 제품 설치 버전 선택 섹션 -->
                <div class="product-install-section" v-if="productList.length > 0">
                    <!-- 제품명 -->
                    <div class="product-name-section">
                        <h2 class="product-name">{{ productName }}</h2>
                    </div>
                    
                    <!-- 버전별 탭 -->
                    <div class="version-tabs">
                        <button
                            @click="selectedVersion = null"
                            :class="['version-tab', { active: selectedVersion === null }]"
                        >
                            전체
                        </button>
                        <button
                            v-for="version in versionList"
                            :key="version"
                            @click="selectedVersion = version"
                            :class="['version-tab', { active: selectedVersion === version }]"
                        >
                            {{ version }}
                        </button>
                    </div>

                    <!-- OS 및 Language 선택 -->
                    <div class="product-options">
                        <div class="option-group">
                            <label>OS</label>
                            <select v-model="selectedOs" class="option-select">
                                <option value="">전체</option>
                                <option
                                    v-for="os in getOsOptionsForVersion(selectedVersion)"
                                    :key="os"
                                    :value="os"
                                >
                                    {{ os }}
                                </option>
                            </select>
                        </div>

                        <div class="option-group">
                            <label>언어</label>
                            <select v-model="selectedLanguage" class="option-select">
                                <option value="">전체</option>
                                <option
                                    v-for="lang in getLanguageOptionsForVersion(selectedVersion, selectedOs)"
                                    :key="lang"
                                    :value="lang"
                                >
                                    {{ lang }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <!-- 다운로드 파일 목록 (모든 파일) -->
                    <div class="additional-downloads-section" v-if="additionalDownloads.length > 0">
                        <h3 class="downloads-title">다운로드 파일</h3>
                        <div class="download-links">
                            <a
                                v-for="download in additionalDownloads"
                                :key="download.idx"
                                :href="download.downloadLink"
                                target="_blank"
                                class="download-link-item"
                            >
                                <span class="download-icon">{{ getFileIcon(download.fileExtension) }}</span>
                                <span class="download-name">{{ download.fileName || `다운로드 (${download.fileExtension})` }}</span>
                                <span class="download-extension">{{ download.fileExtension }}</span>
                            </a>
                        </div>
                    </div>
                </div>

                <!-- 게시글 제목 (게시글이 있는 경우만 표시) -->
                <div class="board-title-section" v-if="boardVO && boardVO.idx">
                    <h1 class="board-title">{{ boardVO.title }}</h1>
                </div>

                <!-- 게시글 내용 (게시글이 있는 경우만 표시) -->
                <div class="board-content-section" v-if="boardVO && boardVO.idx">
                    <div class="board-content">
                        <summernote-frame
                            :target-id="'contentHtml-' + boardVO.idx"
                            ref="summernote"
                            mode="view"
                            :html="boardVO.contentHtml"
                        ></summernote-frame>
                    </div>
                </div>

                <!-- 목록으로 버튼 -->
                <div class="btn-area">
                    <a href="javascript:;" class="btn-listgo" @click="goList">목록으로</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import HWAPP from "@/commons/mixins/hwapp.mixin";
import SummernoteFrame from "@/components/display/summernoteFrame";
import('summernote/dist/summernote.css')
import('summernote/dist/summernote-bs4.css')

export default {
    name: 'productDownDetail',
    components: {
        SummernoteFrame
    },
    mixins: [HWAPP],
    data() {
        return {
            loading: false,
            boardInfoVO: {},
            boardVO: {},
            boardKey: '',
            idx: 0,
            gid: null,
            productList: [],
            selectedVersion: null, // null이면 전체
            selectedOs: '', // ''이면 전체
            selectedLanguage: '' // ''이면 전체
        }
    },
    computed: {
        /**
         * 버전 목록 (중복 제거, 내림차순 정렬)
         */
        versionList() {
            const versions = [...new Set(this.productList.map(p => p.version).filter(v => v))];
            return versions.sort((a, b) => {
                // 숫자로 변환하여 비교 (2025, 2024 등)
                const numA = parseInt(a) || 0;
                const numB = parseInt(b) || 0;
                return numB - numA; // 내림차순
            });
        },
        /**
         * 다운로드 파일 목록 (모든 파일)
         * 옵션이 선택되지 않으면 모든 파일 표시, 선택되면 필터링
         */
        additionalDownloads() {
            let filteredList = this.productList.filter(p => p.downloadLink);
            
            // 버전 필터링
            if (this.selectedVersion) {
                filteredList = filteredList.filter(p => p.version === this.selectedVersion);
            }
            
            // OS 필터링
            if (this.selectedOs) {
                filteredList = filteredList.filter(p => p.osType === this.selectedOs);
            }
            
            // 언어 필터링
            if (this.selectedLanguage) {
                filteredList = filteredList.filter(p => p.language === this.selectedLanguage);
            }

            return filteredList;
        },
        /**
         * 제품명 (첫 번째 제품의 productName 사용)
         */
        productName() {
            if (this.productList.length > 0 && this.productList[0].productName) {
                return this.productList[0].productName;
            }
            return '';
        }
    },
    watch: {
        selectedVersion(newVersion) {
            // 버전 변경 시 OS와 Language는 유지 (전체로 초기화하지 않음)
        },
        selectedOs() {
            // OS 변경 시 Language는 유지 (전체로 초기화하지 않음)
        }
    },
    created() {
        // 라우트 파라미터에서 boardKey와 idx 가져오기
        this.boardKey = this.$route.params.boardKey || '';
        this.idx = parseInt(this.$route.params.idx) || 0;
        
        // 쿼리 파라미터에서 gid 가져오기 (idx가 0인 경우)
        if (this.$route.query && this.$route.query.gid) {
            this.gid = parseInt(this.$route.query.gid) || null;
        }
        
        // idx가 0이고 gid가 있으면 gid로 제품 목록만 조회
        if (this.idx === 0 && this.gid) {
            this.loadProductList();
            return;
        }
        
        if (!this.boardKey || !this.idx) {
            this.swalUtils.gritter('오류', '잘못된 접근입니다.', 'error');
            return;
        }
        
        this.loadBoardData();
    },
    methods: {
        /**
         * 게시판 정보 및 게시글 조회
         */
        async loadBoardData() {
            try {
                this.boardInfoVO = await this.getBoardInfo(this.boardKey);
                this.getBoardOne();
            } catch (e) {
                console.error('게시판 정보 조회 실패:', e);
                this.swalUtils.gritter('오류', '게시판 정보를 불러올 수 없습니다.', 'error');
            }
        },

        /**
         * 게시글 상세 조회
         */
        getBoardOne() {
            if (!this.idx || this.idx <= 0) {
                return;
            }

            this.loading = true;
            this.$eventBus.$emit("startProgress", 1);
            
            this.axiosCaller.get(this, this.APIs.BOARDS + "/getBoardOne/" + this.idx, {}, (res) => {
                const result = res.data;
                if (result.status === "ok") {
                    this.boardVO = result.boardVO;
                    // gid 추출
                    this.gid = this.boardVO.gid;
                    
                    // gid가 있으면 제품 목록 조회
                    if (this.gid != null && this.gid !== 0) {
                        this.loadProductList();
                    }
                } else {
                    this.swalUtils.gritter('오류', result.msg || '게시글을 불러올 수 없습니다.', 'error');
                }
                this.loading = false;
                this.$eventBus.$emit("doneProgress", 1);
                
                this.$nextTick(() => {
                    this.webUtils.scrollTo("#app");
                });
            }, (err) => {
                console.error('게시글 조회 실패:', err);
                this.swalUtils.gritter('오류', '게시글을 불러올 수 없습니다.', 'error');
                this.loading = false;
                this.$eventBus.$emit("doneProgress", 1);
            });
        },

        /**
         * gid로 제품 목록 조회
         */
        loadProductList() {
            if (!this.gid) {
                return;
            }

            this.axiosCaller.get(this, this.APIs.PRODUCT + "/getProductListByGid/" + this.gid, {}, (res) => {
                const result = res.data;
                if (result.status === "ok") {
                    this.productList = result.list || [];
                    // 초기에는 버전을 선택하지 않아서 모든 파일이 표시되도록 함
                } else {
                    console.error('제품 목록 조회 실패:', result.msg);
                }
            }, (err) => {
                console.error('제품 목록 조회 실패:', err);
            });
        },

        /**
         * 선택된 버전의 OS 옵션 목록
         * 버전이 null이면 전체 제품에서 OS 목록 가져오기
         */
        getOsOptionsForVersion(version) {
            let products = this.productList;
            if (version) {
                products = products.filter(p => p.version === version);
            }
            return [...new Set(products.map(p => p.osType).filter(os => os))];
        },

        /**
         * 선택된 버전과 OS의 언어 옵션 목록
         * 버전이나 OS가 선택되지 않으면 전체에서 언어 목록 가져오기
         */
        getLanguageOptionsForVersion(version, os) {
            let products = this.productList;
            if (version) {
                products = products.filter(p => p.version === version);
            }
            if (os) {
                products = products.filter(p => p.osType === os);
            }
            return [...new Set(products.map(p => p.language).filter(lang => lang))];
        },

        /**
         * 파일 확장자에 따른 아이콘 반환
         */
        getFileIcon(extension) {
            const iconMap = {
                'exe': '⚙️',
                'zip': '📦',
                'pdf': '📄',
                'dmg': '💿',
                'pkg': '📦',
                'deb': '📦',
                'rpm': '📦',
                '기타': '📄'
            };
            return iconMap[extension] || '📄';
        },

        /**
         * 목록으로 이동 (제품 설치 페이지로)
         */
        goList() {
            if (this.$router) {
                this.$router.push({
                    name: 'Install'
                });
            } else {
                window.location.href = `/app/product_download`;
            }
        }
    }
};
</script>

<style scoped>
.product-down-detail-page {
    padding: 20px 0;
}

.loading,
.empty {
    text-align: center;
    padding: 40px;
    color: #999;
    font-size: 16px;
}

.product-detail-content {
    max-width: 1200px;
    margin: 0 auto;
}

/* 게시글 제목 섹션 */
.board-title-section {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 2px solid #e0e0e0;
}

.board-title {
    font-size: 28px;
    font-weight: bold;
    color: #333;
    margin: 0;
}

/* 제품 설치 섹션 */
.product-install-section {
    background: #f9f9f9;
    border-radius: 8px;
    padding: 30px;
    margin-bottom: 30px;
}

/* 제품명 섹션 */
.product-name-section {
    margin-bottom: 25px;
}

.product-name {
    font-size: 32px;
    font-weight: bold;
    color: #333;
    margin: 0;
}

/* 버전 탭 */
.version-tabs {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.version-tab {
    padding: 10px 20px;
    background: white;
    border: 2px solid #ddd;
    border-radius: 6px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    color: #666;
    transition: all 0.3s;
}

.version-tab:hover {
    border-color: #007bff;
    color: #007bff;
}

.version-tab.active {
    background: #007bff;
    border-color: #007bff;
    color: white;
}

/* 옵션 선택 */
.product-options {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.option-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.option-group label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.option-select {
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    background: white;
    min-width: 150px;
    cursor: pointer;
}

.option-select:focus {
    outline: none;
    border-color: #007bff;
}

/* 설치 버튼 섹션 */
.install-button-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #ddd;
}

.btn-install {
    padding: 12px 30px;
    background-color: #000;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s;
}

.btn-install:hover:not(:disabled) {
    background-color: #333;
}

.btn-install:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.download-link-info {
    margin-top: 10px;
    font-size: 12px;
    color: #666;
}

.download-link-info a {
    color: #007bff;
    text-decoration: none;
    word-break: break-all;
}

.download-link-info a:hover {
    text-decoration: underline;
}

/* 추가 다운로드 섹션 */
.additional-downloads-section {
    margin-top: 30px;
    padding-top: 30px;
    border-top: 1px solid #ddd;
}

.downloads-title {
    font-size: 18px;
    font-weight: bold;
    color: #333;
    margin-bottom: 15px;
}

.download-links {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.download-link-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 15px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    text-decoration: none;
    color: #333;
    transition: all 0.3s;
}

.download-link-item:hover {
    background: #f5f5f5;
    border-color: #007bff;
    text-decoration: none;
}

.download-icon {
    font-size: 20px;
    flex-shrink: 0;
}

.download-name {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
}

.download-extension {
    padding: 4px 8px;
    background: #e3f2fd;
    color: #1976d2;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
}

/* 게시글 내용 섹션 */
.board-content-section {
    margin-bottom: 30px;
}

.board-content {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 30px;
}

.board-content .inner {
    line-height: 1.8;
    color: #333;
}

/* 목록으로 버튼 */
.btn-area {
    text-align: center;
    margin-top: 30px;
}

/* 반응형 */
@media (max-width: 768px) {
    .board-title {
        font-size: 22px;
    }

    .product-install-section {
        padding: 20px;
    }

    .version-tabs {
        gap: 8px;
    }

    .version-tab {
        padding: 8px 16px;
        font-size: 14px;
    }

    .product-options {
        flex-direction: column;
        gap: 15px;
    }

    .option-select {
        width: 100%;
    }
}
</style>
